import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import Layout from "../components/Layout";
import ComboBox from "../components/ComboBox";
import { CATEGORIES } from "./VehiculesListe";
import { estEtrangere, formaterSaisie, LONGUEUR_MAX, normaliser } from "../immatriculation";

const MARQUES = [
  "MAN", "Volvo", "Renault Trucks", "Iveco", "Mercedes-Benz", "Scania", "DAF",
  "Ford", "Fiat", "Peugeot", "Citroën", "Renault", "Nissan", "Toyota", "Opel",
  "Isuzu", "Mitsubishi", "Fuso", "Volkswagen",
];

const VEHICULE_VIDE = {
  immatriculation: "",
  immatriculation_etrangere: false,
  categorie: "porteur",
  marque: "",
  type_modele: "",
  n_chassis: "",
  n_serie: "",
  date_premiere_circulation: "",
  date_mise_en_service: "",
  // LES DATES DES DERNIERS CONTROLES, a la creation seulement.
  //
  // Elles ne sont pas des champs du vehicule : elles servent de base au calcul
  // de la premiere echeance. Sans elles, l'echeance se deduit de la premiere
  // circulation -- un calcul qui ne decrit qu'un camion NEUF, et qui a produit
  // 88 « retards » sur les 102 vehicules du premier import reel.
  date_dernier_ct: "",
  date_derniere_vgp: "",
  capacite_kg: "",
  centre_gravite_mm: "",
  n_parc: "",
  km_initial: "",
};

const HAYON_VIDE = {
  marque: "",
  type: "",
  n_serie: "",
  annee: "",
  capacite_levage_kg: "",
  centre_gravite_mm: "",
  pression_limiteur_bar: "",
  arret_de_rolls: false,
};

const AUJOURDHUI = new Date().toISOString().slice(0, 10);
const ANNEE_COURANTE = new Date().getFullYear();

// "" -> null pour respecter les Optional du backend.
const txt = (v) => (v === "" || v === null || v === undefined ? null : v);
const nombre = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// Chiffres uniquement (entier positif).
const nettoyerEntier = (s) => s.replace(/[^0-9]/g, "");

// Chiffres + un seul separateur decimal (positif).
function nettoyerDecimal(s) {
  let v = s.replace(",", ".").replace(/[^0-9.]/g, "");
  const i = v.indexOf(".");
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  return v;
}

export default function VehiculeForm({ user, onLogout }) {
  const { id } = useParams();
  const edition = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(VEHICULE_VIDE);
  const [hayon, setHayon] = useState(false);
  const [hayonForm, setHayonForm] = useState(HAYON_VIDE);
  const [erreurs, setErreurs] = useState({});
  const [erreurGlobale, setErreurGlobale] = useState("");
  const [chargement, setChargement] = useState(edition);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!edition) return;
    api
      .get(`/vehicules/${id}`)
      .then(({ data }) => {
        setForm({
          immatriculation: data.immatriculation || "",
          immatriculation_etrangere: estEtrangere(data.immatriculation),
          categorie: data.categorie || "porteur",
          marque: data.marque || "",
          type_modele: data.type_modele || "",
          n_chassis: data.n_chassis || "",
          n_serie: data.n_serie || "",
          date_premiere_circulation: data.date_premiere_circulation || "",
          date_mise_en_service: data.date_mise_en_service || "",
          capacite_kg: data.capacite_kg ?? "",
          centre_gravite_mm: data.centre_gravite_mm ?? "",
          n_parc: data.n_parc || "",
          km_initial: data.km_initial ?? "",
        });
        setHayon(Boolean(data.hayon));
        if (data.equipement_hayon) {
          const h = data.equipement_hayon;
          setHayonForm({
            marque: h.marque || "",
            type: h.type || "",
            n_serie: h.n_serie || "",
            annee: h.annee ?? "",
            capacite_levage_kg: h.capacite_levage_kg ?? "",
            centre_gravite_mm: h.centre_gravite_mm ?? "",
            pression_limiteur_bar: h.pression_limiteur_bar ?? "",
            arret_de_rolls: h.arret_de_rolls ?? false,
          });
        }
      })
      // Un 404 n'est pas une panne (audit B3, 28/09/2026) : la ressource n'existe
      // pas -- ou appartient a un autre client, que le serveur ne confirme
      // jamais. « Impossible de charger » envoyait chercher un probleme de
      // reseau. Meme message que la fiche vehicule.
      .catch((err) =>
        setErreurGlobale(err?.response?.status === 404
          ? "Véhicule introuvable."
          : "Impossible de charger le véhicule."))
      .finally(() => setChargement(false));
  }, [id, edition]);

  const majForm = (champ, valeur) => {
    setForm((f) => ({ ...f, [champ]: valeur }));
    setErreurs((e) => ({ ...e, [champ]: undefined }));
  };
  const majHayon = (champ, valeur) => {
    setHayonForm((h) => ({ ...h, [champ]: valeur }));
    setErreurs((e) => ({ ...e, [`hayon.${champ}`]: undefined }));
  };

  // Validation cote client — renvoie un objet {champ: message}.
  function valider() {
    const e = {};
    const immat = normaliser(form.immatriculation, form.immatriculation_etrangere);
    if (immat.erreur) e.immatriculation = immat.erreur;
    if (form.date_premiere_circulation && form.date_premiere_circulation > AUJOURDHUI) {
      e.date_premiere_circulation = "La date ne peut pas être dans le futur";
    }
    if (form.date_mise_en_service && form.date_mise_en_service > AUJOURDHUI) {
      e.date_mise_en_service = "La date ne peut pas être dans le futur";
    }
    if (
      form.date_premiere_circulation &&
      form.date_mise_en_service &&
      form.date_mise_en_service < form.date_premiere_circulation
    ) {
      e.date_mise_en_service = "Doit être postérieure ou égale à la 1re circulation";
    }
    if (form.date_dernier_ct && form.date_dernier_ct > AUJOURDHUI) {
      e.date_dernier_ct = "Un contrôle déjà passé ne peut pas être daté du futur";
    }
    if (form.date_derniere_vgp && form.date_derniere_vgp > AUJOURDHUI) {
      e.date_derniere_vgp = "Un contrôle déjà passé ne peut pas être daté du futur";
    }
    if (hayon && hayonForm.annee !== "") {
      const a = Number(hayonForm.annee);
      if (a < 1980 || a > ANNEE_COURANTE) {
        e["hayon.annee"] = `Année comprise entre 1980 et ${ANNEE_COURANTE}`;
      }
    }
    return e;
  }

  const soumettre = async (e) => {
    e.preventDefault();
    setErreurGlobale("");

    const erreursLocales = valider();
    if (Object.keys(erreursLocales).length > 0) {
      setErreurs(erreursLocales);
      return;
    }
    setErreurs({});
    setEnvoi(true);

    const payload = {
      organisation_id: user.organisation_id, // impose cote serveur, requis par le schema
      // Forme canonique : c'est elle que le serveur compare pour les doublons.
      immatriculation: normaliser(form.immatriculation, form.immatriculation_etrangere).valeur,
      immatriculation_etrangere: form.immatriculation_etrangere,
      categorie: form.categorie,
      marque: txt(form.marque),
      type_modele: txt(form.type_modele),
      n_chassis: txt(form.n_chassis),
      n_serie: txt(form.n_serie),
      date_premiere_circulation: txt(form.date_premiere_circulation),
      date_mise_en_service: txt(form.date_mise_en_service),
      capacite_kg: nombre(form.capacite_kg),
      centre_gravite_mm: nombre(form.centre_gravite_mm),
      n_parc: txt(form.n_parc),
      km_initial: form.km_initial === "" ? 0 : Number(form.km_initial),
      hayon,
    };

    // A LA CREATION SEULEMENT. En modification, les echeances existent deja et
    // se corrigent depuis la fiche : les renvoyer ici n'aurait aucun effet, et
    // un champ sans effet est un champ qui ment.
    if (!edition) {
      if (form.date_dernier_ct) payload.date_dernier_ct = form.date_dernier_ct;
      if (hayon && form.date_derniere_vgp) {
        payload.date_derniere_vgp = form.date_derniere_vgp;
      }
    }

    if (hayon) {
      payload.equipement_hayon = {
        marque: txt(hayonForm.marque),
        type: txt(hayonForm.type),
        n_serie: txt(hayonForm.n_serie),
        annee: nombre(hayonForm.annee),
        capacite_levage_kg: nombre(hayonForm.capacite_levage_kg),
        centre_gravite_mm: nombre(hayonForm.centre_gravite_mm),
        pression_limiteur_bar: nombre(hayonForm.pression_limiteur_bar),
        arret_de_rolls: hayonForm.arret_de_rolls,
      };
    }

    try {
      if (edition) {
        await api.put(`/vehicules/${id}`, payload);
        navigate(`/vehicules/${id}`);
      } else {
        const { data } = await api.post("/vehicules", payload);
        navigate(`/vehicules/${data.id}`);
      }
    } catch (err) {
      const statut = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (statut === 409) {
        setErreurs({ immatriculation: "Cette immatriculation existe déjà dans votre organisation." });
      } else if (statut === 422 && Array.isArray(detail)) {
        // Mappe chaque erreur FastAPI sur le champ concerne (loc = [body, champ, ...]).
        const parChamp = {};
        let global = "";
        for (const item of detail) {
          const parts = (item.loc || []).filter((x) => x !== "body");
          if (parts.length === 0) {
            global = item.msg; // validation au niveau du modele (ex. coherence des dates)
          } else if (parts[0] === "equipement_hayon") {
            parChamp[`hayon.${parts[1]}`] = item.msg;
          } else {
            parChamp[parts[0]] = item.msg;
          }
        }
        setErreurs(parChamp);
        if (global) setErreurGlobale(global);
      } else {
        setErreurGlobale("Enregistrement impossible. Vérifiez les champs saisis.");
      }
      setEnvoi(false);
    }
  };

  const Err = ({ champ }) =>
    erreurs[champ] ? <span className="erreur-champ">{erreurs[champ]}</span> : null;
  const classeInput = (champ) => (erreurs[champ] ? "champ-invalide" : "");

  if (chargement) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p>Chargement…</p>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h2>{edition ? "Modifier le véhicule" : "Nouveau véhicule"}</h2>
        <button className="btn" onClick={() => navigate(edition ? `/vehicules/${id}` : "/vehicules")}>
          Annuler
        </button>
      </div>

      {erreurGlobale && <div className="erreur">{erreurGlobale}</div>}

      <form className="formulaire" onSubmit={soumettre} noValidate>
        <section className="carte">
          <h3>Informations véhicule</h3>
          <div className="grille-form">
            <div className="champ-immat">
              {/* La case est sur la ligne du libellé, et non sous le champ :
                  sous le champ, elle décalerait vers le bas les champs voisins,
                  alignés par le bas. */}
              <div className="champ-immat-entete">
                <label htmlFor="immatriculation">Immatriculation *</label>
                <label className="case-plaque-etrangere">
                  {/* « Étrangère » seul à l'écran — « Plaque étrangère » ne tient
                      pas sur la ligne d'une colonne de 220 px — mais le nom
                      complet pour un lecteur d'écran. */}
                  <input
                    type="checkbox"
                    aria-label="Plaque étrangère"
                    checked={form.immatriculation_etrangere}
                    onChange={(e) => majForm("immatriculation_etrangere", e.target.checked)}
                  />
                  Étrangère
                </label>
              </div>
              <input
                id="immatriculation"
                required
                className={classeInput("immatriculation")}
                placeholder={form.immatriculation_etrangere ? "Plaque étrangère" : "AA-123-AA"}
                maxLength={LONGUEUR_MAX}
                value={form.immatriculation}
                onChange={(e) =>
                  majForm("immatriculation", formaterSaisie(e.target.value, form.immatriculation_etrangere))
                }
                onBlur={() => {
                  // En quittant le champ, la plaque prend sa forme canonique
                  // (1234AB59 → 1234 AB 59) : ce que l'on voit est ce qui
                  // sera enregistré.
                  const { valeur } = normaliser(form.immatriculation, form.immatriculation_etrangere);
                  if (valeur && valeur !== form.immatriculation) majForm("immatriculation", valeur);
                }}
              />
              <Err champ="immatriculation" />
            </div>
            <label>
              Catégorie *
              <select value={form.categorie} onChange={(e) => majForm("categorie", e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.valeur} value={c.valeur}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Marque
              <ComboBox
                value={form.marque}
                options={MARQUES}
                placeholder="Rechercher ou saisir…"
                onChange={(v) => majForm("marque", v)}
              />
            </label>
            <label>
              Type / modèle
              <input value={form.type_modele} onChange={(e) => majForm("type_modele", e.target.value)} />
            </label>
            <label>
              N° châssis
              <input value={form.n_chassis} onChange={(e) => majForm("n_chassis", e.target.value)} />
            </label>
            <label>
              N° série
              <input value={form.n_serie} onChange={(e) => majForm("n_serie", e.target.value)} />
            </label>
            <label>
              1re circulation
              <input
                type="date"
                max={AUJOURDHUI}
                className={classeInput("date_premiere_circulation")}
                value={form.date_premiere_circulation}
                onChange={(e) => majForm("date_premiere_circulation", e.target.value)}
              />
              <Err champ="date_premiere_circulation" />
            </label>
            <label>
              Mise en service
              <input
                type="date"
                max={AUJOURDHUI}
                min={form.date_premiere_circulation || undefined}
                className={classeInput("date_mise_en_service")}
                value={form.date_mise_en_service}
                onChange={(e) => majForm("date_mise_en_service", e.target.value)}
              />
              <Err champ="date_mise_en_service" />
            </label>
            <label>
              Capacité (kg)
              <input
                inputMode="decimal"
                className={classeInput("capacite_kg")}
                value={form.capacite_kg}
                onChange={(e) => majForm("capacite_kg", nettoyerDecimal(e.target.value))}
              />
              <Err champ="capacite_kg" />
            </label>
            <label>
              Centre de gravité (mm)
              <input
                inputMode="decimal"
                className={classeInput("centre_gravite_mm")}
                value={form.centre_gravite_mm}
                onChange={(e) => majForm("centre_gravite_mm", nettoyerDecimal(e.target.value))}
              />
              <Err champ="centre_gravite_mm" />
            </label>
            <label>
              N° parc
              <input value={form.n_parc} onChange={(e) => majForm("n_parc", e.target.value)} />
            </label>
            <label>
              Km initial
              <input
                inputMode="numeric"
                className={classeInput("km_initial")}
                value={form.km_initial}
                onChange={(e) => majForm("km_initial", nettoyerEntier(e.target.value))}
              />
              <Err champ="km_initial" />
            </label>
          </div>

          <label className="case-hayon">
            <input type="checkbox" checked={hayon} onChange={(e) => setHayon(e.target.checked)} />
            Ce véhicule est équipé d'un hayon élévateur
          </label>
        </section>

        {hayon && (
          <section className="carte">
            <h3>Hayon élévateur</h3>
            <div className="grille-form">
              <label>
                Marque
                <input value={hayonForm.marque} onChange={(e) => majHayon("marque", e.target.value)} />
              </label>
              <label>
                Type
                <input value={hayonForm.type} onChange={(e) => majHayon("type", e.target.value)} />
              </label>
              <label>
                N° série
                <input value={hayonForm.n_serie} onChange={(e) => majHayon("n_serie", e.target.value)} />
              </label>
              <label>
                Année
                <input
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="1980 – aujourd'hui"
                  className={classeInput("hayon.annee")}
                  value={hayonForm.annee}
                  onChange={(e) => majHayon("annee", nettoyerEntier(e.target.value).slice(0, 4))}
                />
                <Err champ="hayon.annee" />
              </label>
              <label>
                Capacité de levage (kg)
                <input
                  inputMode="decimal"
                  className={classeInput("hayon.capacite_levage_kg")}
                  value={hayonForm.capacite_levage_kg}
                  onChange={(e) => majHayon("capacite_levage_kg", nettoyerDecimal(e.target.value))}
                />
                <Err champ="hayon.capacite_levage_kg" />
              </label>
              <label>
                Centre de gravité (mm)
                <input
                  inputMode="decimal"
                  className={classeInput("hayon.centre_gravite_mm")}
                  value={hayonForm.centre_gravite_mm}
                  onChange={(e) => majHayon("centre_gravite_mm", nettoyerDecimal(e.target.value))}
                />
                <Err champ="hayon.centre_gravite_mm" />
              </label>
              <label>
                Pression limiteur (bar)
                <input
                  inputMode="decimal"
                  className={classeInput("hayon.pression_limiteur_bar")}
                  value={hayonForm.pression_limiteur_bar}
                  onChange={(e) => majHayon("pression_limiteur_bar", nettoyerDecimal(e.target.value))}
                />
                <Err champ="hayon.pression_limiteur_bar" />
              </label>
            </div>
            <label className="case-hayon">
              <input
                type="checkbox"
                checked={hayonForm.arret_de_rolls}
                onChange={(e) => majHayon("arret_de_rolls", e.target.checked)}
              />
              Arrêt de rolls
            </label>
          </section>
        )}

        {/* LA SECTION QUI MANQUAIT, ET QUI COUTAIT LE PLUS CHER.
            Elle n'apparait qu'a la CREATION : ensuite, les echeances existent
            et se corrigent depuis la fiche du vehicule. */}
        {!edition && (
          <section className="carte">
            <h3>Derniers contrôles effectués</h3>
            <p className="note-info">
              Si vous les connaissez, les prochaines échéances en découlent. Sinon,
              le logiciel calcule une date à partir de la mise en circulation — elle
              sera marquée « à renseigner » plutôt qu’affichée comme un retard,
              parce qu’un camion repris en cours de vie a déjà passé des contrôles
              dont nous n’avons pas trace.
            </p>
            <div className="grille-form">
              <label>
                Dernier contrôle technique
                <input
                  type="date"
                  max={AUJOURDHUI}
                  className={classeInput("date_dernier_ct")}
                  value={form.date_dernier_ct}
                  onChange={(e) => majForm("date_dernier_ct", e.target.value)}
                />
                <Err champ="date_dernier_ct" />
              </label>
              {hayon && (
                <label>
                  Dernière VGP du hayon
                  <input
                    type="date"
                    max={AUJOURDHUI}
                    className={classeInput("date_derniere_vgp")}
                    value={form.date_derniere_vgp}
                    onChange={(e) => majForm("date_derniere_vgp", e.target.value)}
                  />
                  <Err champ="date_derniere_vgp" />
                </label>
              )}
            </div>
          </section>
        )}

        <div className="actions-form">
          <button className="btn-accent" type="submit" disabled={envoi}>
            {envoi ? "Enregistrement…" : edition ? "Enregistrer les modifications" : "Créer le véhicule"}
          </button>
        </div>
      </form>
    </Layout>
  );
}
